import React, { useState, useEffect, useMemo } from 'react';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText, Box, alpha, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';

// Create animations - mirroring MagicTextField for consistency
const focusShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Shake animation for error state
const shake = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-2px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(2px);
  }
`;

// No need to define our own glow effect - the theme already has a global one defined in CssBaseline
// The animation 'glow' is defined in components.js and uses the secondary color by default

// Styled InputLabel to match MagicTextField label exactly
const StyledInputLabel = styled(InputLabel, {
  shouldForwardProp: (prop) => prop !== 'magical',
})(({ theme, magical, error }) => {
  const colors = theme.palette;
  return {
    color: alpha(colors.text.primary, theme.palette.mode === 'dark' ? 0.9 : 0.87),
    fontWeight: 500,
    zIndex: 1,
    opacity: 1, // Ensure labels are visible
    
    // Ensure transform is properly set for label positioning
    ...(magical && {
      transform: 'translate(14px, -9px) scale(0.75)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
        opacity: 1,
        visibility: 'visible',
      },
    }),
    
    // Make label visible with proper styling in all states
    '&.MuiFormLabel-filled, &.MuiInputLabel-shrink': {
      opacity: 1,
      visibility: 'visible',
    },
    
    // Consolidated styling for all states - fixes duplicate label issue
    '&.Mui-focused, &.Mui-error, &.Mui-focused.Mui-error': {
      opacity: 1,
      visibility: 'visible',
      color: error 
        ? alpha(colors.error.main, theme.palette.mode === 'dark' ? 1 : 0.9)
        : alpha(theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main, theme.palette.mode === 'dark' ? 1 : 0.9),
      fontWeight: 600,
      letterSpacing: error ? '0' : '0.01em',
    },
  };
});

// Styled FormControl container to match MagicTextField exactly
const StyledFormControl = styled(FormControl)(({ theme, magical, focused, error, color = 'primary' }) => {
  const colors = theme.palette;
  const borderRadius = theme.components?.MuiOutlinedInput?.styleOverrides?.root?.borderRadius || 16;

  return {
    width: '100%',
    marginBottom: theme.spacing(2),
    
    // Base styles for the input field
    '& .MuiOutlinedInput-root': {
      borderRadius,
      transition: 'all 0.3s ease',
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(colors.background.paper, 0.5)  // Darker mode - slightly lighter than background
        : alpha(colors.background.paper, 0.95),  // Light mode - nearly white for better contrast with text
        
      // Improved notched outline for better label visibility
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.mode === 'dark' 
          ? alpha(colors.divider, 0.4)
          : alpha(colors.divider, 0.5),
        borderWidth: '1px',
        transition: 'border-color 0.3s ease',
        // Ensure the notch for the label is properly sized
        legend: {
          // Increase width to prevent label truncation
          '& > span': {
            opacity: 1,
            padding: '0 4px',
            maxWidth: '100%',
            width: 'auto',
            display: 'block',
            visibility: 'visible',
          }
        }
      },
      
      // Standard hover state
      '&:hover:not(.Mui-focused):not(.Mui-error)': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main, theme.palette.mode === 'dark' ? 0.4 : 0.5),
          borderWidth: '1.5px',
        },
      },
      
      // Magical focused state with enforced no-shadow
      ...(magical && {
        // IMPORTANT: Force no box shadow in ALL states to prevent label issues
        boxShadow: 'none !important',
        animation: 'none', // Remove animation for better visibility

        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: (focused && !error) ?
            (theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main) :
            (theme.palette.mode === 'dark' ? alpha(colors.divider, 0.4) : alpha(colors.divider, 0.5)),
          borderWidth: '1px',
          borderRadius: 'inherit',
          transition: 'border-color 0.3s ease',
        },
      }),
      
      // Standard focused state
      ...(!magical && focused && !error && {
        boxShadow: 'none',

        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main, theme.palette.mode === 'dark' ? 0.8 : 0.7),
          borderWidth: theme.palette.mode === 'dark' ? '2px' : '1.5px',
          transition: 'all 0.3s ease',
        },
      }),
      
      // Error state
      '&.Mui-error': {
        boxShadow: 'none',
        animation: `${shake} 0.6s ease-in-out`,

        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(colors.error.main, theme.palette.mode === 'dark' ? 0.5 : 0.6),
          borderWidth: theme.palette.mode === 'dark' ? '2px' : '1.5px',
          borderStyle: 'solid',
        },

        // Add subtle background highlight for error state
        '& .MuiSelect-select': {
          backgroundColor: alpha(colors.error.main, theme.palette.mode === 'dark' ? 0.03 : 0.01),
        },
      },
      
      // Customize select input
      '& .MuiSelect-select': {
        padding: '12px 16px',
        color: theme.palette.text.primary,
        transition: 'background-color 0.3s ease',
        // ...(magical && focused && !error && {
        //   backgroundColor: theme.palette.mode === 'dark'
        //     ? alpha(colors.secondary.main, 0.015)
        //     : alpha(colors.primary.main, 0.05),
        // }),
      },
    },
    
    // Enhanced label styles - critical for consistency
    '& .MuiInputLabel-root': {
      transition: 'color 0.3s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      color: alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.7 : 0.9), // Default color to match TextField
      
      // Make labels appear shrunk by default when magical is true
      ...(magical && {
        transform: 'translate(14px, -9px) scale(0.75)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75)',
        },
      }),
      
      '&.Mui-focused:not(.Mui-error)': {
        color: alpha(theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main, theme.palette.mode === 'dark' ? 1 : 0.9),
        fontWeight: 500,
        letterSpacing: '0.01em',
      },
      
      '&.Mui-error': {
        color: alpha(colors.error.main, theme.palette.mode === 'dark' ? 1 : 0.9),
        fontWeight: 500,
      },
    },
    
    // Helper text styling with better visibility
    '& .MuiFormHelperText-root': {
      marginLeft: theme.spacing(1.5),
      marginTop: 4,
      fontSize: '0.75rem',
      transition: 'color 0.25s ease',
      color: alpha(colors.text.secondary, theme.palette.mode === 'dark' ? 0.7 : 0.8),

      // Simple color change on focus for better readability
      '&.Mui-focused:not(.Mui-error)': {
        color: alpha(colors.text.secondary, theme.palette.mode === 'dark' ? 0.9 : 1),
      },

      // Clear error styling
      '&.Mui-error': {
        color: alpha(colors.error.main, theme.palette.mode === 'dark' ? 1 : 0.9),
        fontWeight: 500,
        // Removed background for better readability
        marginTop: 6,
      },
    },
    
    // Ensure helper text never shows twice
    '& .MuiFormHelperText-root + .MuiFormHelperText-root': {
      display: 'none',
    },
    
    // Menu and MenuItem styling for consistent dropdown appearance
    '& .MuiMenuItem-root': {
      padding: '10px 16px',
      fontSize: '0.875rem',
      transition: 'background-color 0.2s ease',
      
      '&:hover': {
        backgroundColor: alpha(theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main, theme.palette.mode === 'dark' ? 0.05 : 0.08),
      },
      
      '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.15),
        color: theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main,
        fontWeight: 500,
        
        '&:hover': {
          backgroundColor: alpha(theme.palette.mode === 'dark' ? colors.secondary.main : colors.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.2),
        },
      },
      
      '&.Mui-disabled': {
        opacity: 0.6,
        color: theme.palette.text.disabled,
      },
    },
    
    // Style for the popup menu paper to ensure it fits properly
    '& .MuiMenu-paper': {
      marginTop: 8,
      width: 'auto',
      minWidth: '100%', // At least as wide as the select input
      borderRadius: 8,
      boxShadow: theme.shadows[3],
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    },
  };
});

/**
 * MagicSelect - Enhanced select field component with consistent styling and animations
 * 
 * @param {Object} props - Component properties
 * @param {string} props.name - Field name (required)
 * @param {string} props.label - Field label
 * @param {Array} props.options - Array of option objects with value and label properties
 * @param {any} props.value - Current value
 * @param {Function} props.onChange - Change handler function
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.error - Whether the field has an error
 * @param {string} props.helperText - Helper text to display
 * @param {string} props.placeholder - Placeholder text for empty selection
 * @param {string} props.optionLabelKey - Key to use for option label (default: 'label')
 * @param {string} props.optionValueKey - Key to use for option value (default: 'value')
 * @param {boolean} props.fullWidth - Whether the field should take full width
 * @param {Function} props.renderOption - Custom render function for menu items
 * @param {Object} props.formControlProps - Additional props for FormControl
 * @param {Object} props.InputProps - Additional props for the Input component
 * @param {boolean} props.magical - Whether to apply magical effects (glowing, animations)
 * @param {string} props.color - Color theme ('primary', 'secondary', etc.)
 */
const MagicSelect = ({
  name,
  label,
  options = [],
  value,
  onChange,
  required = false,
  error = false,
  helperText = '',
  placeholder = '',
  optionLabelKey = 'label',
  optionValueKey = 'value',
  fullWidth = true,
  renderOption,
  formControlProps = {},
  InputProps = {},
  magical = true, // Default to true for backward compatibility
  color = 'primary',
  loading = false, // Add loading prop
  ...props
}) => {
  // Remove magical and loading from props to avoid passing them to DOM elements
  const { magical: _, loading: __, ...selectProps } = props;
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  
  
  // Handle focus events
  const handleFocus = (e) => {
    setFocused(true);
    if (props.onFocus) props.onFocus(e);
  };
  
  const handleBlur = (e) => {
    setFocused(false);
    if (props.onBlur) props.onBlur(e);
  };
  
  // Handle change event 
  const handleChange = (event) => {
    if (onChange) {
      // We need to extract the exact selected value from the event
      // The raw event.target.value should contain the option value from the selection
      const rawValue = event.target.value;
      
      // Sanity check the options to make sure they're properly formatted
      // Note: normalizedOptions is not yet defined here, we'll log later
      
      // SPECIAL CASE FOR CLIENT/BRAND SELECTION - DIRECT VALUE PASS
      if (name === 'clientId' || name === 'brandId') {
        onChange(name, rawValue);
        return;
      }
      
      // For all other fields
      onChange(rawValue, name);
    }
  };

  // Normalize options format with failsafe for missing values
  // First, ensure we have a valid array and filter out completely invalid entries
  const safeOptions = Array.isArray(options) ? options.filter(option => option != null) : [];
  
  const normalizedOptions = safeOptions.map((option, index) => {
    // Detect malformed options missing value property
    let normalizedOption;
    
    if (typeof option === 'object' && option !== null) {
      // Check if the option is missing a value property
      if (option[optionValueKey] === undefined || option[optionValueKey] === null) {
        // Add a synthetic value based on the object properties or index
        if (option.id !== undefined && option.id !== null) {
          normalizedOption = {
            value: String(option.id),
            label: option[optionLabelKey] || `Option ${index + 1}`,
            ...option
          };
        } else if (option._id !== undefined && option._id !== null) {
          normalizedOption = {
            value: String(option._id),
            label: option[optionLabelKey] || `Option ${index + 1}`,
            ...option
          };
        } else {
          // Last resort: create a synthetic ID based on index and label
          const syntheticId = `option-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
          normalizedOption = {
            value: syntheticId,
            label: option[optionLabelKey] || `Option ${index + 1}`,
            ...option,
            _syntheticId: true
          };
        }
      } else {
        // Normal case: option has value property
        normalizedOption = {
          value: option[optionValueKey],
          label: option[optionLabelKey] || option[optionValueKey] || `Option ${index + 1}`,
          ...option
        };
      }
    } else {
      // Handle primitive options (strings, numbers)
      normalizedOption = {
        value: option,
        label: String(option)
      };
    }
    
    // CRITICAL: Ensure value is never undefined or null
    if (normalizedOption.value === undefined || normalizedOption.value === null) {
      normalizedOption.value = `option-${index}-${Date.now()}`;
    }
    
    // Ensure label is never undefined or null
    if (normalizedOption.label === undefined || normalizedOption.label === null) {
      normalizedOption.label = String(normalizedOption.value);
    }
    
    return normalizedOption;
  }).filter(option => option && option.value !== undefined && option.value !== null);
  
  // Final safety check - if we have no valid options, provide an empty state
  // Use useMemo to stabilize the finalOptions array and prevent unnecessary re-renders
  const finalOptions = useMemo(() => {
    return normalizedOptions.length > 0 ? normalizedOptions : [];
  }, [normalizedOptions]);

  // When magical is true, make sure the label appears shrunk by default
  const inputLabelProps = magical ? {
    shrink: true,
    disableAnimation: false // Enable animation for better visibility
  } : {};

  // Wrap render in try-catch to identify which component causes errors
  try {
    return (
      <Box sx={{ width: '100%', mb: 0 }}>
      <StyledFormControl 
        fullWidth={fullWidth} 
        error={error} 
        required={required}
        magical={magical}
        focused={magical || focused}
        color={color}
        {...formControlProps}
      >
        {label && (
          <StyledInputLabel 
            id={`${name}-label`} 
            focused={magical || focused} 
            error={Boolean(error)}
            magical={magical}
            {...inputLabelProps}
          >
            {label}
          </StyledInputLabel>
        )}
        
        <Select
          labelId={`${name}-label`}
          id={name}
          name={name}
          value={value || ''}
          label={label}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          displayEmpty={!!placeholder}
          key={`select-${name}-${finalOptions.length}`}
          native={false}
          variant="outlined"
          inputProps={{
            ...InputProps
          }}
          sx={{
            '& .MuiSelect-icon': {
              color: theme.palette.mode === 'dark' ? undefined : theme.palette.primary.main,
              opacity: 1,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.mode === 'dark' ? undefined : alpha(theme.palette.primary.main, 0.7),
            },
            '& .MuiSelect-select': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 48 * 4.5, // Display 4.5 items
                marginTop: 8, // Add margin to avoid touching the select
                borderRadius: 8, // Match the theme's border radius
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: theme.shadows[3], // Add proper shadow for popover effect
              },
              sx: {
                '& .MuiMenu-list': {
                  padding: '4px 0',
                },
              }
            },
            // Ensure menu properly positions itself and matches width of parent
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            // Fix menu width issues to prevent overflow
            sx: {
              '& .MuiPaper-root': {
                width: 'auto', 
                minWidth: 'calc(100% - 4px)', // Match parent width with slight adjustment
                overflowX: 'hidden'
              }
            }
          }}
          {...selectProps}
        >
          {(() => {
            // Isolate children rendering in an IIFE to prevent stale closures
            const safeOptions = finalOptions.filter(option => 
              option && 
              option.value !== undefined && 
              option.value !== null &&
              typeof option.value !== 'undefined'
            );
            
            const children = [];
            
            // Add placeholder first
            if (placeholder) {
              children.push(
                <MenuItem 
                  key="placeholder-item"
                  value="" 
                  disabled 
                  sx={{ 
                    opacity: 0.6, 
                    fontStyle: 'italic', 
                    color: theme.palette.text.secondary 
                  }}
                >
                  {placeholder}
                </MenuItem>
              );
            }
            
            // Add options
            safeOptions.forEach((option, index) => {
              const optionValue = option.value;
              
              // Skip invalid options
              if (optionValue === undefined || optionValue === null) {
                return;
              }
              
              // Ensure value is string or number
              let safeValue = optionValue;
              if (typeof optionValue !== 'string' && typeof optionValue !== 'number') {
                safeValue = String(optionValue);
              }
              
              children.push(
                <MenuItem 
                  key={`${name}-option-${index}-${String(safeValue)}`} 
                  value={safeValue}
                >
                  {renderOption ? renderOption(option) : option.label}
                </MenuItem>
              );
            });
            
            return children;
          })()}
        </Select>
        
        {helperText && (
          <FormHelperText>{helperText}</FormHelperText>
        )}
      </StyledFormControl>
    </Box>
  );
  } catch (error) {
    console.error(`Error in MagicSelect "${name}":`, error);
    
    // Return a minimal error display component
    return (
      <Box sx={{ 
        width: '100%', 
        p: 2, 
        border: '2px solid red', 
        borderRadius: 1,
        backgroundColor: 'rgba(255, 0, 0, 0.1)' 
      }}>
        <Typography color="error" variant="body2">
          Error in MagicSelect "{name}": {error.message}
        </Typography>
      </Box>
    );
  }
};

export default MagicSelect;